import { Fragment, useEffect, useMemo, useState } from 'react';
import * as amplitude from '@amplitude/analytics-browser';

import {
  Avatar,
  Button,
  Input,
  Modal,
  StatusIndicator,
} from '@unified-checkout/design-system';
import { Check } from '@unified-checkout/design-system/icons/index';
import type { ITagProps } from 'libs/design-system/src/types/tag/tag';

import type {
  ISharedModalProps,
  TShareModalSocialButtonsMap,
} from '@app/dashboard/types/shared/modals/share-modal';
import type { IButton } from 'libs/design-system/src/types/Button/button';
import { phoneWithoutCountryCode } from '@app/dashboard/utils/phoneWithoutCountryCode';
import { COUNTRIES } from '@app/dashboard/constants/countries';
import { emailOrPhoneValidation } from '@app/dashboard/utils/emailPhoneValidation';
import { normalizeEgyptPhone } from '@app/dashboard/utils/normalizeEgyptPhone';
import { useLangStore } from '@app/dashboard/store/lang';
import {
  COUNTRY_NAMES,
  DEFAULT_LOCALIZATION,
  DEFAULT_SOCIAL_MEDIA_BTNS,
  EMPTY_DEFAULT_TAGS,
  INPUT_STATUS,
  SOCIAL_MEDIA,
} from '@app/dashboard/constants/shared/modals/share-modal';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import CopyText from '@app/dashboard/components/inner_layout/CopyText';
import { handleShareViaSocial } from '@app/dashboard/utils/shareModal';
import { truncateString } from '@app/dashboard/utils/truncateString';

import QRCodeModal from '../QRCodeModal';

import styles from './share-modal.module.scss';

const ShareModal = ({
  modalProps: {
    isModalOpen,
    title,
    subtitle,
    handleCloseModal,
    isOverlayAboveModal,
    hasCloseIcon,
    headerIcon,
    width = '35rem',
  },
  shareInputProps: { link, label, linkLength = 30 },
  socialBtns = {},
  inputTagsProps: {
    label: tagsLabel,
    placeholder: tagsPlaceholder,
    hintText: tagsHintText,
    defaultTags = EMPTY_DEFAULT_TAGS,
    showTagsInput = true,
    onTagValidator,
    tagsShareBtn,
    sentLabel,
    resetTagsOnShare,
  } = {},
  QRCodeProps,
  handleDefaultSocialBtnEvents,
  onCopyTextHandler,
}: ISharedModalProps) => {
  const { lang } = useLangStore();
  const country = getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY) || 'EGY';
  const countryName = COUNTRY_NAMES[country]?.[lang] ?? COUNTRY_NAMES['EGY'][lang];

  const [isQRCodeOpen, setIsQRCodeOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [tags, setTags] = useState<ITagProps[]>([]);
  const [sharedContacts, setSharedContacts] = useState<ITagProps[]>([]);
  const [inputState, setInputState] = useState<{
    status: Lowercase<keyof typeof INPUT_STATUS>;
    message?: string;
  }>({ status: INPUT_STATUS.DEFAULT, message: '' });

  const socialBtnsMemo = useMemo(() => {
    //configur defaukt social media buttons with their default handlers,
    const defaultBtnsMap = DEFAULT_SOCIAL_MEDIA_BTNS.reduce<
      Record<string, IButton>
    >((acc, btn) => {
      const btnId = btn.id as string;

      if (btnId === SOCIAL_MEDIA.QR_CODE) {
        acc[btnId] = {
          ...btn,
          onClickHandler: () => {
            setIsQRCodeOpen(true);
            handleDefaultSocialBtnEvents?.(btnId);
          },
        };
        return acc;
      }

      acc[btnId] = {
        ...btn,
        onClickHandler: () => {
          handleShareViaSocial({
            platform: btnId,
            shareableLink: link as string,
          });
          handleDefaultSocialBtnEvents?.(btnId);
        },
      };

      return acc;
    }, {});

    const mergedBtns = {
      ...defaultBtnsMap,
      ...socialBtns,
    } as TShareModalSocialButtonsMap;

    // filter out hidden buttons if (hide proprety true), sort them based on sortOrder prop then ovride default config with the one from props
    return Object.entries(mergedBtns)
      .filter(([, btnConfig]) => !btnConfig?.hide)
      .sort(([, a], [, b]) => {
        return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      })
      .map(([btnKey, btnConfig]) => ({
        ...(defaultBtnsMap[btnKey] ?? {}),
        ...btnConfig,
        id: btnConfig.id ?? btnKey,
      }));
  }, [socialBtns, link]);

  const handleCopyText = (copied: boolean) => {
    setIsCopied(copied);
    onCopyTextHandler?.(link as string);
  };

  const handleTagAdd = (tag: ITagProps) => {
    const { isEgypt, normalized } = normalizeEgyptPhone(tag.text);

    let newTag = tag.text.includes('@') || !isEgypt ? tag.text : normalized;

    setTags((prevTags) => [...prevTags, { ...tag, text: newTag }]);
  };

  const handleTagRemove = (tagToRemove: ITagProps) => {
    setTags((prevTags) =>
      prevTags.filter(
        (tag) => tag.id !== tagToRemove.id || tag.text !== tagToRemove.text
      )
    );
  };

  const handleTagValidator = (rawValue: ITagProps | string) => {
    const value =
      typeof rawValue === 'string' ? rawValue.trim() : rawValue.text.trim();

    const isEmail =
      typeof rawValue === 'string'
        ? rawValue.includes('@')
        : rawValue.text.includes('@');

    const { pattern, error_msg } = emailOrPhoneValidation();
    const { normalized } = normalizeEgyptPhone(value);

    let finalStatus = {
      status: INPUT_STATUS.DEFAULT as Lowercase<keyof typeof INPUT_STATUS>,
      message: '',
    };

    if (
      value &&
      !pattern.test(
        !isEmail ? phoneWithoutCountryCode(value, COUNTRIES.EGY) : value
      )
    ) {
      finalStatus = {
        status: INPUT_STATUS.ERROR,
        message: error_msg,
      };
    } else if (
      [...tags, ...sharedContacts].some(
        (tag) => tag.text === (!isEmail ? normalized : value)
      )
    ) {
      finalStatus = {
        status: INPUT_STATUS.ERROR,
        message: DEFAULT_LOCALIZATION[lang].duplicateData,
      };
    } else {
      finalStatus = {
        status: INPUT_STATUS.DEFAULT,
        message: '',
      };
    }

    setInputState(finalStatus);
    return finalStatus.status !== INPUT_STATUS.ERROR;
  };

  useEffect(() => {
    if (QRCodeProps?.onQRCodeOpenChange) {
      QRCodeProps.onQRCodeOpenChange(isQRCodeOpen);
    }
  }, [isQRCodeOpen]);

  useEffect(() => {
    setSharedContacts(defaultTags);
  }, [defaultTags]);

  useEffect(() => {
    if (resetTagsOnShare) {
      setSharedContacts([...sharedContacts, ...tags]);
      setTags([]);
    }
  }, [resetTagsOnShare]);

  return (
    <Modal
      open={isModalOpen}
      title={title || DEFAULT_LOCALIZATION[lang].title}
      supportingText={subtitle || DEFAULT_LOCALIZATION[lang].subTitle}
      onCloseHandler={handleCloseModal}
      position="center"
      width={width}
      isOverlayAboveModal={isOverlayAboveModal}
      hasCloseIcon={tagsShareBtn?.loading ? false : hasCloseIcon}
      headerIcon={
        headerIcon || <StatusIndicator status="success" icon={Check} />
      }
      wrapperClassName={styles['share-modal']}
      closeOnClickOutside={false}
    >
      <div className={styles['share-modal__body']}>
        <div className={styles['share-modal__body-wrapper']}>
          <div className={styles['share-modal__body-input']}>
            <Input
              id="shareable-link"
              name="shareable-link"
              type="text"
              value={truncateString(
                link,
                isCopied ? (linkLength ? linkLength - 5 : 25) : linkLength || 30
              )}
              label={label || DEFAULT_LOCALIZATION[lang].shareLabel}
              disabled
            />
            <CopyText
              textToCopy={link}
              showTextToCopy={false}
              onCopyChange={handleCopyText}
            />
          </div>
          {!!socialBtnsMemo.length && (
            <div className={styles['share-modal__body-social-btns']}>
              {socialBtnsMemo?.map((btn) => {
                return (
                  <Fragment key={btn.id}>
                    <Button {...btn} />
                  </Fragment>
                );
              })}
            </div>
          )}
        </div>
        {showTagsInput && (
          <div
            className={styles['share-modal__body-tags']}
            data-shared-tags-disabled={tagsShareBtn?.disabled}
            data-shared-tags-error={inputState.status === INPUT_STATUS.ERROR}
          >
            <Input
              type="tags"
              id="shareable-link-tags"
              name="shareable-link-tags"
              placeholder={
                tagsPlaceholder || DEFAULT_LOCALIZATION[lang].tagsPlaceholder
              }
              label={tagsLabel || DEFAULT_LOCALIZATION[lang].tagsLabel}
              hintText={tagsHintText || DEFAULT_LOCALIZATION[lang].tagsHintText(countryName)}
              tags={tags}
              onTagAdd={handleTagAdd}
              onTagRemove={handleTagRemove}
              error={inputState.message}
              status={inputState.status}
              tagValidator={onTagValidator || handleTagValidator}
              disabled={tagsShareBtn?.disabled || false}
              onChange={(e) => {
                const value = e.target.value;
                onTagValidator
                  ? onTagValidator(value)
                  : handleTagValidator(value);
              }}
            />
            <Button
              text={
                tagsShareBtn?.text || DEFAULT_LOCALIZATION[lang].tagsShareBtn
              }
              variant="outlined"
              color="primary"
              size="md"
              {...tagsShareBtn}
              onClickHandler={() => tagsShareBtn?.onClickHandler(tags)}
              disabled={tagsShareBtn?.disabled || !tags.length}
            />
          </div>
        )}
        {!!sharedContacts.length &&
          sharedContacts.map((contact, index) => (
            <div
              className={styles['share-modal__body-shared-contacts']}
              key={index}
            >
              <Avatar />
              <div className={styles['share-modal__body-shared-contacts-info']}>
                <span
                  className={
                    styles['share-modal__body-shared-contacts-info-value']
                  }
                >
                  {contact.text}
                </span>
                <span
                  className={
                    styles['share-modal__body-shared-contacts-info-status']
                  }
                >
                  {sentLabel || DEFAULT_LOCALIZATION[lang].sent}
                </span>
              </div>
            </div>
          ))}
      </div>
      <QRCodeModal
        {...QRCodeProps}
        modalProps={{
          ...QRCodeProps?.modalProps,
          isModalOpen: isQRCodeOpen,
          handleCloseModal: () => setIsQRCodeOpen(false),
        }}
        modalBodyRef={QRCodeProps?.modalBodyRef!}
        cardProps={{
          ...QRCodeProps?.cardProps,
          QRCodeValue: QRCodeProps?.cardProps?.QRCodeValue || link || '',
        }}
      />
    </Modal>
  );
};

export default ShareModal;
